import { useId } from 'react';
import { Controller } from 'react-hook-form';
import { Button, Form, Input, Label, Modal } from '@/shared/components';
import { useCustomiseTemplateForm } from '../hooks/useCustomiseTemplateForm';
import type { DocumentTemplateSummary } from '../types/documentTemplates.types';
import { DealerOwnerSelect } from './DealerOwnerSelect';

interface Props {
  source: DocumentTemplateSummary;
  isOpen: boolean;
  onClose: () => void;
}

export function CustomiseTemplateModal({ source, isOpen, onClose }: Props) {
  const headingId = useId();
  const { form, submit, isSaving } = useCustomiseTemplateForm(source, onClose);

  return (
    <Modal isOpen={isOpen} onClose={onClose} ariaLabelledBy={headingId} className="max-w-lg p-6">
      <h2 id={headingId} className="mb-1 text-lg font-semibold">Customise template</h2>
      <p className="mb-4 text-sm text-mp-muted">
        Creates a dealer-owned copy of the published version of {source.name}. The original does not change.
      </p>
      <Form className="space-y-4" onSubmit={submit}>
        <div>
          <Label htmlFor="customise-dealer">Dealer</Label>
          <Controller
            name="dealerId"
            control={form.control}
            render={({ field, fieldState: { error } }) => (
              <>
                <DealerOwnerSelect id="customise-dealer" value={field.value || null} onChange={(id) => field.onChange(id ?? '')} />
                {error && (
                  <p role="alert" className="mt-1.5 text-xs text-mp-error">
                    {error.message}
                  </p>
                )}
              </>
            )}
          />
        </div>
        <div>
          <Label htmlFor="customise-name">Name</Label>
          <Controller
            name="name"
            control={form.control}
            render={({ field, fieldState: { error } }) => (
              <Input
                id="customise-name"
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                error={!!error}
                hint={error?.message}
                hintId="customise-name-error"
                aria-invalid={!!error}
                aria-describedby={error ? 'customise-name-error' : undefined}
              />
            )}
          />
        </div>
        <div>
          <Label htmlFor="customise-key">Key</Label>
          <Controller
            name="key"
            control={form.control}
            render={({ field, fieldState: { error } }) => (
              <Input
                id="customise-key"
                className="font-mono"
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                error={!!error}
                hint={error?.message}
                hintId="customise-key-error"
                aria-invalid={!!error}
                aria-describedby={error ? 'customise-key-error' : undefined}
              />
            )}
          />
        </div>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="clear" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={isSaving}>
            {isSaving ? 'Customising...' : 'Customise'}
          </Button>
        </div>
      </Form>
    </Modal>
  );
}
